/**
 * Composer-Text: Im Eingabefeld stehen lesbare Token (`@Funke`, `#allgemein`, `:feuer:`), gesendet wird die
 * Rohsyntax (`<@id>`, `<#id>`, `<:feuer:id>`). Die Zuordnung entsteht bei der Autovervollständigung bzw. beim
 * Öffnen einer Nachricht zum Bearbeiten. Code (Inline/Block) bleibt unverändert.
 */
import type { EmojiDto } from '@lagerfeuer/shared';

/** Anzeige-Token → Rohsyntax. */
export type TokenMap = Record<string, string>;

const CODE_SPLIT = /(```[\s\S]*?```|`[^`\n]*`)/g;

function mapOutsideCode(text: string, fn: (part: string) => string): string {
  return text
    .split(CODE_SPLIT)
    .map((part, i) => (i % 2 === 1 ? part : fn(part)))
    .join('');
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Ersetzt Anzeige-Token und `:name:`-Kürzel eigener Emojis durch die Rohsyntax. */
export function toRaw(text: string, map: TokenMap, emojiByName?: (name: string) => Pick<EmojiDto, 'id' | 'name' | 'animated'> | undefined): string {
  const keys = Object.keys(map)
    .filter((k) => k.length > 1)
    .sort((a, b) => b.length - a.length);
  return mapOutsideCode(text, (part) => {
    let out = part;
    if (keys.length) {
      const re = new RegExp(`(^|[^\\w])(${keys.map(escapeRe).join('|')})(?![\\w])`, 'g');
      out = out.replace(re, (_m, pre: string, tok: string) => `${pre}${map[tok] ?? tok}`);
    }
    if (emojiByName) {
      out = out.replace(/<a?:[A-Za-z0-9_]{2,32}:\d{1,20}>|:([A-Za-z0-9_]{2,32}):/g, (m, name: string | undefined) => {
        if (!name) return m;
        const e = emojiByName(name);
        return e ? `<${e.animated ? 'a' : ''}:${e.name}:${e.id}>` : m;
      });
    }
    return out;
  });
}

export interface DisplayResolvers {
  user(id: string): { name: string; username: string } | null;
  role(id: string): string | null;
  channel(id: string): string | null;
}

/** Rohsyntax → lesbarer Text (für „Bearbeiten“); füllt `map` für den Rückweg. */
export function toDisplay(raw: string, r: DisplayResolvers, map: TokenMap): string {
  const register = (preferred: string, fallback: string, value: string): string => {
    const key = map[preferred] === undefined || map[preferred] === value ? preferred : fallback;
    map[key] = value;
    return key;
  };
  return mapOutsideCode(raw, (part) =>
    part
      .replace(/<@!?(\d{1,20})>/g, (m, id: string) => {
        const u = r.user(id);
        return u ? register(`@${u.name}`, `@${u.username}`, `<@${id}>`) : m;
      })
      .replace(/<@&(\d{1,20})>/g, (m, id: string) => {
        const name = r.role(id);
        return name ? register(`@${name}`, m, m) : m;
      })
      .replace(/<#(\d{1,20})>/g, (m, id: string) => {
        const name = r.channel(id);
        return name ? register(`#${name}`, m, m) : m;
      })
      .replace(/<(a?):([A-Za-z0-9_]{2,32}):(\d{1,20})>/g, (m, _a: string, name: string) => register(`:${name}:`, m, m)),
  );
}

// ---------- Autovervollständigung ----------

export interface Trigger {
  kind: '@' | '#' | ':';
  query: string;
  /** Index des Auslösezeichens im Text. */
  start: number;
  /** Cursorposition (Ende des Tokens). */
  end: number;
}

/** Erkennt ein offenes `@…`, `#…` oder `:…` direkt vor dem Cursor (nicht in Code). */
export function findTrigger(text: string, caret: number): Trigger | null {
  const before = text.slice(0, caret);
  // In offenem Code keine Vorschläge.
  const ticks = (before.match(/`/g) ?? []).length;
  if (ticks % 2 === 1) return null;
  const m = /(^|[\s([{])([@#:])([^\s@#:<>`]{0,32})$/.exec(before);
  if (!m) return null;
  const kind = m[2] as Trigger['kind'];
  const query = m[3] ?? '';
  if (kind === ':' && query.length < 2) return null;
  return { kind, query, start: caret - query.length - 1, end: caret };
}

/** Ersetzt das Token des Auslösers durch `insert` (+ Leerzeichen); liefert neuen Text und Cursor. */
export function applyCompletion(text: string, t: Trigger, insert: string): { text: string; caret: number } {
  const after = text.slice(t.end);
  const sep = after.startsWith(' ') ? '' : ' ';
  const next = `${text.slice(0, t.start)}${insert}${sep}${after}`;
  return { text: next, caret: t.start + insert.length + 1 };
}
